import { create } from "zustand";
import {
  clearInFlightPagination,
  paginateMessages,
} from "../utils/paginateMessages";
import {
  getLessonsList,
  getMessagesByReactions,
  getLessonsByScore,
  postMessageToFavorite,
  getFavoriteMessages,
  getLessonsByCompetence,
  searchByText,
  getUserMessagePagination,
  getLessonPagination,
  getInlineButtons,
  postMessageToTag,
  getTagMessages,
  getMessageByTag,
  getLessonsToDo,
  getCrashCoursesList,
  postMessageFeedbackSent,
} from "../axios/chat";
import { HcpMentionSendOptions, Message, MessagePrev } from "../types";
import { createMessage } from "../utils";
import useAppStore from "./appStore";
import {
  buildMessageCacheKey,
  loadMessagesFromCache,
  saveMessagesToCache,
  scheduleMessageCacheSave,
  clearMessageCache,
  flushMessageCacheSave,
  filterLessonFeedMessages,
} from "../utils/messageCache";

import type { InlineButton } from "../axios/chat";
import { hasAuthToken } from "../utils/authToken";

export type { InlineButton, InlineButtonsData } from "../axios/chat";

const LESSONS_CACHE_KEY = "coachmate-lessons-cache";

const readCachedLessons = (): MessagePrev[] => {
  try {
    if (!hasAuthToken()) return [];
    const stored = localStorage.getItem(LESSONS_CACHE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored) as MessagePrev[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeCachedLessons = (lessons: MessagePrev[]) => {
  try {
    localStorage.setItem(LESSONS_CACHE_KEY, JSON.stringify(lessons));
  } catch {
    // ignore
  }
};

const messagesFromCachedLessonsList = (): Message[] => {
  const { domain, focus } = useAppStore.getState();

  return readCachedLessons()
    .filter(
      (lesson) =>
        lesson.type === "tabbed_message" ||
        (Array.isArray(lesson.tabbed_content) && lesson.tabbed_content.length),
    )
    .map((lesson) => ({
      ...lesson,
      is_bot: true,
      user_id: "",
      message_type: "default" as const,
      domain: lesson.domain || domain,
      focus: lesson.focus || focus,
    }));
};

const getLessonTitleFromRecord = (
  msg: MessagePrev | Message | Record<string, unknown> | undefined | null,
): string => {
  if (!msg || typeof msg !== "object") return "";
  const rec = msg as Record<string, unknown>;
  const candidates = [
    rec.title,
    rec.lesson_title,
    rec.lessonTitle,
    rec.tabbed_title,
    rec.tabbedTitle,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  const tabbed = rec.tabbed_content;
  if (Array.isArray(tabbed)) {
    for (const t of tabbed) {
      if (!t || typeof t !== "object") continue;
      const tr = t as Record<string, unknown>;
      const tabCandidates = [
        tr.title,
        tr.lesson_title,
        tr.lessonTitle,
        tr.tabbed_title,
        tr.tabbedTitle,
      ];
      for (const c of tabCandidates) {
        if (typeof c === "string" && c.trim()) return c.trim();
      }
    }
  }
  return "";
};

const mergeLessonTitleCache = (
  prev: Record<string, string>,
  items: Array<MessagePrev | Message | Record<string, unknown>>,
) => {
  const next = { ...prev };
  for (const item of items) {
    const title = getLessonTitleFromRecord(item);
    if (!title) continue;
    const rec = item as Record<string, unknown>;
    const id = typeof rec.message_id === "string" ? rec.message_id : "";
    if (id) next[id] = title;
  }
  return next;
};

type ReactedLessonCacheItem = MessagePrev & { reaction: string };

const mergeReactedLessonCache = (
  prev: Record<string, ReactedLessonCacheItem>,
  items: Array<MessagePrev | Message | Record<string, unknown>>,
  reactionHint?: string,
) => {
  const next = { ...prev };
  for (const item of items) {
    const rec = item as Record<string, unknown>;
    const id = typeof rec.message_id === "string" ? rec.message_id : "";
    if (!id) continue;

    const type = typeof rec.type === "string" ? rec.type : "";
    const tabbedContent = rec.tabbed_content;
    const isTabbed =
      type === "tabbed_message" ||
      (Array.isArray(tabbedContent) && tabbedContent.length > 0);
    if (!isTabbed) continue;

    const reaction =
      typeof rec.reaction === "string" && rec.reaction.trim()
        ? rec.reaction.trim()
        : reactionHint || "";
    if (!reaction) continue;

    next[id] = { ...(item as MessagePrev), reaction };
  }
  return next;
};

interface ChatState {
  messages: Message[];
  lessons: MessagePrev[];
  reactedMessages: MessagePrev[];
  lessonsByScore: MessagePrev[];

  favoriteMessages: MessagePrev[];
  tagMessages: string[];

  lessonsByCompetence: MessagePrev[];
  messageByTag: MessagePrev[];

  searchResults: MessagePrev[];

  clearInfoPanelResults: () => void;
  messageAudio: string | null;
  messageAudioId: string | null;
  isLoadingOlder: boolean;
  isLoadingNewer: boolean;
  isLoadingLessons: boolean;
  isLoadingReactions: boolean;
  isLoadingLessonsByScore: boolean;

  isLoadingFavoriteMessages: boolean;
  isLoadingTagMessages: boolean;

  isLoadingLessonsByCompetence: boolean;
  isLoadingMessageByTag: boolean;

  isLoadingSearch: boolean;
  errorLessons: string | null;
  errorReactions: string | null;
  errorLessonsByScore: string | null;

  errorFavoriteMessages: string | null;
  errorTagMessages: string | null;

  errorLessonsByCompetence: string | null;
  errorMessageByTag: string | null;

  errorSearch: string | null;
  targetMessageId: string | null;
  targetMessageTabId: string | null;
  targetMessageRequestSeq: number;
  refetchTargetMessage: boolean;
  copyMessage: string | null;
  setCopyMessage: (copyMessage: string | null) => void;
  fetchInitialMessages: (targetMessageId?: string) => Promise<Message[]>;
  bootstrapChat: (options?: {
    force?: boolean;
    targetMessageId?: string;
  }) => Promise<Message[]>;
  fetchInitialMessagesWithoutClearing: (
    targetMessageId?: string,
  ) => Promise<Message[]>;

  loadOlderMessages: () => Promise<Message[] | null>;
  loadNewerMessages: () => Promise<Message[] | null>;
  fetchLessons: () => Promise<void>;
  fetchReactions: (reaction: string) => Promise<void>;
  fetchLessonsByScore: (score: string) => Promise<void>;

  fetchFavoriteMessages: () => Promise<void>;
  fetchTagMessages: () => Promise<void>;

  fetchLessonsByCompetence: (competence: string) => Promise<void>;
  fetchMessageByTag: (tag: string) => Promise<void>;

  fetchSearchResults: (text: string) => Promise<void>;
  fetchUserMessagePagination: (
    user_message_id?: string,
    direction?: "up" | "down",
  ) => Promise<{ up: boolean; down: boolean; message_id: string | null }>;
  fetchLessonPagination: (
    message_id?: string,
    direction?: "up" | "down",
  ) => Promise<{ up: boolean; down: boolean; message_id: string | null }>;
  setMessages: (msgs: Message[] | ((prev: Message[]) => Message[])) => void;
  useHistory: boolean | null;
  setUseHistory: (useHistory: boolean | null) => void;
  setTargetMessageId: (id: string | null, tabId?: string | null) => void;
  waitForAnimation: boolean;
  setWaitForAnimation: (value: boolean) => void;
  sendMessage: (
    text: string,
    user: any,
    sendSocket: (msg: Message) => void,
    hideFromUser?: boolean,
    options?: HcpMentionSendOptions,
  ) => Promise<void>;
  uploadFile: (
    file: File,
    type: "image" | "file",
    uploadFunc: (file: File) => Promise<void>,
  ) => Promise<void>;

  updateFavoriteStatus: (
    messageId: string,
    isSaved: boolean,
    tabLabels?: string[],
  ) => Promise<void>;
  favoriteTabLabelsCache: Record<string, string[]>;
  lessonTitleCache: Record<string, string>;
  reactedLessonCache: Record<string, ReactedLessonCacheItem>;
  cacheTabbedMessageLabels: (messageId: string, tabLabels: string[]) => void;
  cacheLessonTitle: (
    messageId: string,
    domain: string,
    focus: string,
    title: string,
  ) => void;
  updateTagStatus: (messageId: string, tagText: string[]) => Promise<void>;

  updateFeedbackSentStatus: (
    ZmessageId: string,
    feedbackSent: boolean,
  ) => Promise<void>;

  isOpenMiniPlayer: boolean;
  handleToggleMiniPlayer: () => Promise<void>;
  setMessagesAudio: (
    messageAudio: string | null,
    messageAudioId: string | null,
  ) => Promise<void>;
  clearInfoMessages: () => void;
  clearMessageAudio: () => Promise<void>;
  clearChatData: () => Promise<void>;
  cleanMessageArray: () => Promise<void>;
  persistMessagesToCacheFor: (
    domain: string,
    focus: string,
    messageTheme: string,
  ) => Promise<void>;
  setIsOpenMiniPlayer: (b: boolean) => Promise<void>;
  activeAudioPlayerId: string | null;
  activeAudioPlayerPause: (() => void) | null;
  setActiveAudioPlayer: (id: string | null) => void;
  setActiveAudioPlayerPause: (pause: (() => void) | null) => void;

  inlineMenuTitle: string;
  inlineButtons: InlineButton[];
  setInlineButtons: (buttons: InlineButton[]) => void;
  fetchInlineButtons: () => Promise<void>;
  clearChatInfoData: () => Promise<void>;

  scheduleTrim: (direction: "up" | "down") => void;
  trimTimer: ReturnType<typeof setTimeout> | undefined;

  canPaginateDown: boolean;
  canPaginateUp: boolean;
  isBootstrapping: boolean;
  lastBootstrapAt: number;
  bootstrapGeneration: number;

  lessonsToDo: MessagePrev[];
  isLoadingLessonsToDo: boolean;
  errorLessonsToDo: string | null;
  fetchLessonsToDo: () => Promise<void>;

  crashCourses: MessagePrev[];
  isLoadingCrashCourses: boolean;
  errorCrashCourses: string | null;
  fetchCrashCourses: () => Promise<void>;
  refreshCrashCourses: () => Promise<void>;

  refreshScheduled: boolean;
  setRefreshScheduled: (isScheduled: boolean) => void;
  scheduledTargetMessageId: string | null;
  setScheduledTargetMessageId: (messageId: string | null) => void;
}

let bootstrapInFlight: Promise<Message[]> | null = null;

export const useChatStore = create<ChatState>((set, get) => ({
  trimTimer: undefined,
  canPaginateDown: false,
  canPaginateUp: true,
  isBootstrapping: false,
  lastBootstrapAt: 0,
  bootstrapGeneration: 0,
  messages: [],
  useHistory: null,
  setUseHistory: (useHistory) => set({ useHistory }),
  lessons: readCachedLessons(),
  reactedMessages: [],
  lessonsByScore: [],

  favoriteMessages: [],
  favoriteTabLabelsCache: (() => {
    try {
      const stored = localStorage.getItem("favoriteTabLabelsCache");
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, string[]>;
        return parsed && typeof parsed === "object" ? parsed : {};
      }
    } catch {
      // ignore
    }
    return {};
  })(),
  lessonTitleCache: (() => {
    try {
      const stored = localStorage.getItem("lessonTitleCache");
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, string>;
        return parsed && typeof parsed === "object" ? parsed : {};
      }
    } catch {
      // ignore
    }
    return {};
  })(),
  reactedLessonCache: (() => {
    try {
      const stored = localStorage.getItem("reactedLessonCache");
      if (stored) {
        const parsed = JSON.parse(stored) as Record<
          string,
          ReactedLessonCacheItem
        >;
        return parsed && typeof parsed === "object" ? parsed : {};
      }
    } catch {
      // ignore
    }
    return {};
  })(),
  tagMessages: [],

  lessonsByCompetence: [],
  messageByTag: [],

  searchResults: [],
  isLoadingOlder: false,
  isLoadingNewer: false,
  isLoadingLessons: false,
  isLoadingReactions: false,
  isLoadingLessonsByScore: false,

  isLoadingFavoriteMessages: false,
  isLoadingTagMessages: false,

  isLoadingLessonsByCompetence: false,
  isLoadingMessageByTag: false,

  isLoadingSearch: false,
  errorLessons: null,
  errorReactions: null,
  errorLessonsByScore: null,

  errorFavoriteMessages: null,
  errorTagMessages: null,

  errorLessonsByCompetence: null,
  errorMessageByTag: null,

  errorSearch: null,
  targetMessageId: null,
  targetMessageTabId: null,
  targetMessageRequestSeq: 0,
  refetchTargetMessage: false,
  copyMessage: null,
  setCopyMessage: (copyMessage: string | null) => set({ copyMessage }),

  activeAudioPlayerId: null,
  activeAudioPlayerPause: null,
  setActiveAudioPlayer: (id: string | null) => set({ activeAudioPlayerId: id }),
  setActiveAudioPlayerPause: (pause: (() => void) | null) =>
    set({ activeAudioPlayerPause: pause }),

  setMessages: async (
    action: Message[] | ((prev: Message[]) => Message[]),
  ) => {
    let messagesToCache: Message[] = [];
    set((state) => {
      const newMessages =
        typeof action === "function" ? action(state.messages) : action;
      messagesToCache = newMessages;
      const lessonTitleCache = mergeLessonTitleCache(
        state.lessonTitleCache,
        newMessages as Message[],
      );
      const reactedLessonCache = mergeReactedLessonCache(
        state.reactedLessonCache,
        newMessages as Message[],
      );
      try {
        localStorage.setItem(
          "lessonTitleCache",
          JSON.stringify(lessonTitleCache),
        );
        localStorage.setItem(
          "reactedLessonCache",
          JSON.stringify(reactedLessonCache),
        );
      } catch {
        // ignore
      }
      return { messages: newMessages, lessonTitleCache, reactedLessonCache };
    });
    scheduleMessageCacheSave(
      useAppStore.getState().messageTheme === "lessons"
        ? filterLessonFeedMessages(messagesToCache)
        : messagesToCache,
    );
  },

  setTargetMessageId: (id: string | null, tabId: string | null = null) => {
    set((state) => ({
      targetMessageId: id,
      targetMessageTabId: id ? tabId : null,
      targetMessageRequestSeq: state.targetMessageRequestSeq + 1,
      refetchTargetMessage: !state.refetchTargetMessage,
    }));
  },

  fetchInitialMessages: async (targetMessageId) => {
    const { focus, domain } = useAppStore.getState();
    if (!focus || !domain) {
      throw new Error("fetchInitialMessages blocked: focus/domain not ready");
    }

    const hydrateFromCache = async (): Promise<Message[] | null> => {
      const { messageTheme } = useAppStore.getState();
      const cacheKey = buildMessageCacheKey();
      if (!cacheKey) return null;

      let cached = await loadMessagesFromCache(cacheKey);

      if (messageTheme === "lessons") {
        cached = filterLessonFeedMessages(cached);
        if (!cached.length) {
          const fromLessonsList = messagesFromCachedLessonsList();
          if (!fromLessonsList.length) return null;
          set({ messages: fromLessonsList, canPaginateUp: false });
          return fromLessonsList;
        }
      }

      if (!cached.length) return null;

      set({ messages: cached, canPaginateUp: true });
      return cached;
    };

    try {
      if (!targetMessageId && !navigator.onLine) {
        const cached = await hydrateFromCache();
        if (cached) return cached;
      }

      let initial: Message[] = [];
      if (targetMessageId) {
        initial = await paginateMessages({
          message_id: targetMessageId,
          direction: "middle",
          number: 30,
        });
      } else {
        initial = await paginateMessages({
          direction: "down",
          number: 30,
        });
      }
      set({ messages: initial, canPaginateUp: true });

      const cacheKey = buildMessageCacheKey();
      if (cacheKey) {
        void saveMessagesToCache(cacheKey, initial);
      }

      return initial;
    } catch (error) {
      console.error("Failed to fetch initial messages:", error);
      const cached = await hydrateFromCache();
      if (cached) return cached;
      throw error;
    }
  },

  bootstrapChat: async (
    options: { force?: boolean; targetMessageId?: string } = {},
  ) => {
    const { force = false, targetMessageId } = options;
    const { focus, domain } = useAppStore.getState();

    // Avoid /pagination with empty focus/domain (backend returns [] and UI sticks empty).
    if (!focus || !domain) {
      throw new Error("Chat bootstrap blocked: focus/domain not ready");
    }

    if (targetMessageId) {
      return get().fetchInitialMessages(targetMessageId);
    }

    if (!force) {
      if (bootstrapInFlight) {
        return bootstrapInFlight;
      }

      const { messages, lastBootstrapAt, isBootstrapping } = get();
      if (isBootstrapping && bootstrapInFlight) {
        return bootstrapInFlight;
      }

      if (
        messages.length > 0 &&
        lastBootstrapAt > 0 &&
        Date.now() - lastBootstrapAt < 5000
      ) {
        return messages;
      }
    }

    set({ isBootstrapping: true });
    bootstrapInFlight = (async () => {
      try {
        const loaded = await get().fetchInitialMessages();
        // Empty initial page after login switch is often a race (stale in-flight /
        // missing headers). Do not stamp lastBootstrapAt so useInitialMessages can retry.
        const bootstrapped = loaded.length > 0;
        set((state) => ({
          lastBootstrapAt: bootstrapped ? Date.now() : 0,
          bootstrapGeneration: bootstrapped
            ? state.bootstrapGeneration + 1
            : state.bootstrapGeneration,
          isBootstrapping: false,
        }));
        return loaded;
      } catch (error) {
        set({ isBootstrapping: false });
        throw error;
      } finally {
        bootstrapInFlight = null;
      }
    })();

    return bootstrapInFlight;
  },

  fetchInitialMessagesWithoutClearing: async (targetMessageId) => {
    try {
      let initial: Message[] = [];
      if (targetMessageId) {
        initial = await paginateMessages({
          message_id: targetMessageId,
          direction: "middle",
          number: 30,
        });
      } else {
        initial = await paginateMessages({
          direction: "down",
          number: 30,
        });
      }
      let uniqueInitial: Message[] = initial;
      set((state) => {
        const existingIds = new Set(
          state.messages.map((m) => m?.message_id).filter(Boolean),
        );
        uniqueInitial = initial.filter((m) => {
          const id = m?.message_id;
          return Boolean(id) && !existingIds.has(id as string);
        });
        return {
          messages: [...state.messages, ...uniqueInitial],
          canPaginateUp: true,
        };
      });
      return uniqueInitial;
    } catch (error) {
      console.error("Failed to fetch initial messages:", error);
      throw error;
    }
  },

  loadOlderMessages: async () => {
    const { messages, isLoadingOlder, scheduleTrim } = get();
    if (isLoadingOlder || !messages.length) return null;

    set({ isLoadingOlder: true });
    try {
      const oldestId = messages[0].message_id;
      const older = await paginateMessages({
        message_id: oldestId,
        direction: "up",
        number: 30,
      });

      if (!older.length) {
        set({ canPaginateUp: false });
        return [];
      }

      // De-dupe and ensure pagination makes progress (backend may include the anchor message again)
      const existingIds = new Set(
        messages.map((m) => m?.message_id).filter(Boolean),
      );
      const uniqueOlder = older.filter((m) => {
        const id = m?.message_id;
        return Boolean(id) && !existingIds.has(id as string);
      });

      if (uniqueOlder.length) {
        set((s) => ({ messages: [...uniqueOlder, ...s.messages] }));
        scheduleMessageCacheSave(get().messages);
        scheduleTrim("up");
        return uniqueOlder;
      }

      set({ canPaginateUp: false });
      return [];
    } catch (err) {
      console.error("Error loading older messages:", err);
      set({ canPaginateUp: false });
      return [];
    } finally {
      set({ isLoadingOlder: false });
    }
  },

  loadNewerMessages: async () => {
    const { messages, isLoadingNewer, scheduleTrim } = get();
    if (isLoadingNewer || !messages.length) return null;

    set({ isLoadingNewer: true });
    try {
      const newestId = messages[messages.length - 1].message_id;
      const newer = await paginateMessages({
        message_id: newestId,
        direction: "down",
        number: 30,
      });

      if (!newer.length) {
        set({ canPaginateDown: false });
        return [];
      }

      // De-dupe and ensure pagination makes progress (backend may include the anchor message again)
      const existingIds = new Set(
        messages.map((m) => m?.message_id).filter(Boolean),
      );
      const uniqueNewer = newer.filter((m) => {
        const id = m?.message_id;
        return Boolean(id) && !existingIds.has(id as string);
      });

      if (uniqueNewer.length) {
        set((s) => ({ messages: [...s.messages, ...uniqueNewer] }));
        scheduleMessageCacheSave(get().messages);
        scheduleTrim("down");
        return uniqueNewer;
      }

      set({ canPaginateDown: false });
      return [];
    } catch (err) {
      console.error("Error loading newer messages:", err);
      set({ canPaginateDown: false });
      return [];
    } finally {
      set({ isLoadingNewer: false });
    }
  },

  scheduleTrim: (direction: "up" | "down") => {
    const { trimTimer } = get();
    if (trimTimer) clearTimeout(trimTimer);

    const id = setTimeout(() => {
      set((state) => {
        if (state.messages.length <= 120) return {};

        state.messages =
          direction === "up"
            ? state.messages.slice(0, 120)
            : state.messages.slice(-120);

        if (direction === "up") {
          state.canPaginateDown = true;
        } else if (direction === "down") {
          state.canPaginateUp = true;
        }

        return { trimTimer: undefined };
      });
    }, 1500);

    set({ trimTimer: id });
  },

  fetchLessons: async () => {
    set({ isLoadingLessons: true, errorLessons: null });
    try {
      const lessonsData = await getLessonsList();
      writeCachedLessons(lessonsData);
      set((state) => {
        const lessonTitleCache = mergeLessonTitleCache(
          state.lessonTitleCache,
          lessonsData,
        );
        try {
          localStorage.setItem(
            "lessonTitleCache",
            JSON.stringify(lessonTitleCache),
          );
        } catch {
          // ignore
        }
        return {
          lessons: lessonsData,
          isLoadingLessons: false,
          lessonTitleCache,
        };
      });
    } catch (error) {
      console.error("Error fetching lessons list:", error);
      const cachedLessons = readCachedLessons();
      set({
        lessons: cachedLessons,
        errorLessons: cachedLessons.length ? null : "Error fetching lessons",
        isLoadingLessons: false,
      });
    }
  },

  fetchReactions: async (reaction: string) => {
    set({ isLoadingReactions: true, errorReactions: null });
    try {
      const reactedData = await getMessagesByReactions(reaction);
      set((state) => {
        const localReactedTabbed = state.messages.filter(
          (m) => m.type === "tabbed_message" && m.reaction === reaction,
        );
        const cachedReactedTabbed = Object.values(
          state.reactedLessonCache,
        ).filter((m) => m.reaction === reaction);

        const mergedById = new Map<string, MessagePrev>();

        for (const item of cachedReactedTabbed) {
          if (!item?.message_id) continue;
          mergedById.set(item.message_id, item);
        }

        // Keep local reacted tabbed lessons so they do not disappear after lesson completion.
        for (const item of localReactedTabbed) {
          if (!item?.message_id) continue;
          mergedById.set(item.message_id, item);
        }

        // API payload is the source of truth; enrich existing local item if needed.
        for (const item of reactedData) {
          if (!item?.message_id) continue;
          const prev = mergedById.get(item.message_id);
          mergedById.set(item.message_id, prev ? { ...prev, ...item } : item);
        }

        const mergedReactedData = Array.from(mergedById.values()).sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );

        const lessonTitleCache = mergeLessonTitleCache(
          state.lessonTitleCache,
          mergedReactedData,
        );
        const reactedLessonCache = mergeReactedLessonCache(
          state.reactedLessonCache,
          mergedReactedData,
          reaction,
        );
        try {
          localStorage.setItem(
            "lessonTitleCache",
            JSON.stringify(lessonTitleCache),
          );
          localStorage.setItem(
            "reactedLessonCache",
            JSON.stringify(reactedLessonCache),
          );
        } catch {
          // ignore
        }
        return {
          reactedMessages: mergedReactedData,
          isLoadingReactions: false,
          lessonTitleCache,
          reactedLessonCache,
        };
      });
    } catch (error) {
      console.error("Error fetching messages by reactions:", error);
      set({
        errorReactions: "Error fetching reactions",
        isLoadingReactions: false,
      });
    }
  },

  fetchLessonsByScore: async (score: string) => {
    set({ isLoadingLessonsByScore: true, errorLessonsByScore: null });
    try {
      const data = await getLessonsByScore(score);
      set({ lessonsByScore: data, isLoadingLessonsByScore: false });
    } catch (error) {
      console.error("Error fetching lessons by score:", error);
      set({
        errorLessonsByScore: "Error fetching lessons by score",
        isLoadingLessonsByScore: false,
      });
    }
  },

  fetchFavoriteMessages: async () => {
    set({ isLoadingFavoriteMessages: true, errorFavoriteMessages: null });
    try {
      const favoriteData = await getFavoriteMessages();
      set((state) => {
        const lessonTitleCache = mergeLessonTitleCache(
          state.lessonTitleCache,
          favoriteData,
        );
        try {
          localStorage.setItem(
            "lessonTitleCache",
            JSON.stringify(lessonTitleCache),
          );
        } catch {
          // ignore
        }
        return {
          favoriteMessages: favoriteData,
          isLoadingFavoriteMessages: false,
          lessonTitleCache,
        };
      });
    } catch (error) {
      console.error("Error fetching favorite messages:", error);
      set({
        errorFavoriteMessages: "Error fetching favorite messages",
        isLoadingFavoriteMessages: false,
      });
    }
  },
  fetchTagMessages: async () => {
    set({ isLoadingTagMessages: true, errorTagMessages: null });
    try {
      const tagData = await getTagMessages();
      set({ tagMessages: tagData, isLoadingTagMessages: false });
    } catch (error) {
      console.error("Error fetching favorite messages:", error);
      set({
        errorTagMessages: "Error fetching favorite messages",
        isLoadingTagMessages: false,
      });
    }
  },

  fetchLessonsByCompetence: async (competence: string) => {
    set({ isLoadingLessonsByCompetence: true, errorLessonsByCompetence: null });
    try {
      const data = await getLessonsByCompetence(competence);
      set((state) => {
        const lessonTitleCache = mergeLessonTitleCache(
          state.lessonTitleCache,
          data,
        );
        try {
          localStorage.setItem(
            "lessonTitleCache",
            JSON.stringify(lessonTitleCache),
          );
        } catch {
          // ignore
        }
        return {
          lessonsByCompetence: data,
          isLoadingLessonsByCompetence: false,
          lessonTitleCache,
        };
      });
    } catch (error) {
      console.error("Error fetching lessons by competence:", error);
      set({
        errorLessonsByCompetence: "Error fetching lessons by competence",
        isLoadingLessonsByCompetence: false,
      });
    }
  },
  fetchMessageByTag: async (tag: string) => {
    set({ isLoadingMessageByTag: true, errorMessageByTag: null });
    try {
      const data = await getMessageByTag(tag);
      const sorted = [...data].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      );
      set((state) => {
        const lessonTitleCache = mergeLessonTitleCache(
          state.lessonTitleCache,
          sorted,
        );
        try {
          localStorage.setItem(
            "lessonTitleCache",
            JSON.stringify(lessonTitleCache),
          );
        } catch {
          // ignore
        }
        return {
          messageByTag: sorted,
          isLoadingMessageByTag: false,
          lessonTitleCache,
        };
      });
    } catch (error) {
      console.error("Error fetching lessons by competence:", error);
      set({
        errorMessageByTag: "Error fetching lessons by competence",
        isLoadingMessageByTag: false,
      });
    }
  },

  fetchSearchResults: async (text: string) => {
    set({ isLoadingSearch: true, errorSearch: null });
    try {
      const results = await searchByText(text);
      set({ searchResults: results, isLoadingSearch: false });
    } catch (error) {
      console.error("Error searching messages:", error);
      set({
        errorSearch: "Error searching messages",
        isLoadingSearch: false,
      });
    }
  },

  fetchUserMessagePagination: async (
    user_message_id?: string,
    direction?: "up" | "down",
  ) => {
    try {
      const response = await getUserMessagePagination({
        user_message_id,
        direction,
      });
      if (response.message_id) {
        set({
          targetMessageId: response.message_id,
          targetMessageTabId: null,
          targetMessageRequestSeq: get().targetMessageRequestSeq + 1,
        });
      }
      return response;
    } catch (error) {
      console.error("Error fetching user message pagination:", error);
      return { up: false, down: false, message_id: null };
    }
  },

  fetchLessonPagination: async (
    message_id?: string,
    direction?: "up" | "down",
  ) => {
    try {
      const response = await getLessonPagination({ message_id, direction });
      if (response.message_id) {
        set({
          targetMessageId: response.message_id,
          targetMessageTabId: null,
          targetMessageRequestSeq: get().targetMessageRequestSeq + 1,
        });
      }
      return response;
    } catch (error) {
      console.error("Error fetching lesson pagination:", error);
      return { up: false, down: false, message_id: null };
    }
  },

  sendMessage: async (
    text,
    user,
    sendSocket,
    hideFromUser = false,
    options,
  ) => {
    const { connectionError } = useAppStore.getState();
    if (connectionError) {
      console.warn(
        "Cannot send message - connection error exists:",
        connectionError,
      );
      return Promise.resolve();
    }

    const { focus, domain } = useAppStore.getState();
    const { useHistory, setUseHistory } = get();
    const useHistoryForSend = options?.useHistory ?? useHistory ?? true;
    return new Promise<void>(async (resolve, reject) => {
      try {
        const newMessage: Message = createMessage(
          text,
          "text",
          user,
          domain,
          focus,
          {
            ...options,
            useHistory: useHistoryForSend,
          },
        );
        newMessage.isLoading = true;

        await sendSocket(newMessage);

        setUseHistory(null);

        if (!hideFromUser) {
          set((state) => {
            const updatedMessages = [...state.messages, newMessage];
            resolve();
            return { messages: updatedMessages };
          });
        } else {
          resolve();
        }
      } catch (error) {
        console.error("Error sending message:", error);
        reject(error);
      }
    });
  },

  uploadFile: async (file, type, uploadFunc) => {
    try {
      await uploadFunc(file);
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  },

  updateFavoriteStatus: async (
    messageId: string,
    isSaved: boolean,
    tabLabels?: string[],
  ) => {
    try {
      await postMessageToFavorite(messageId, isSaved);
      set((state) => {
        const cache = { ...state.favoriteTabLabelsCache };
        if (isSaved && tabLabels?.length) cache[messageId] = tabLabels;
        else delete cache[messageId];
        try {
          localStorage.setItem("favoriteTabLabelsCache", JSON.stringify(cache));
        } catch {
          // ignore
        }
        return { favoriteTabLabelsCache: cache };
      });
      await get().fetchFavoriteMessages();
    } catch (error) {
      console.error("Error updating favorite status:", error);
    }
  },

  cacheTabbedMessageLabels: (messageId: string, tabLabels: string[]) => {
    if (!tabLabels?.length) return;
    set((state) => {
      const cache = { ...state.favoriteTabLabelsCache, [messageId]: tabLabels };
      try {
        localStorage.setItem("favoriteTabLabelsCache", JSON.stringify(cache));
      } catch {
        // ignore
      }
      return { favoriteTabLabelsCache: cache };
    });
  },

  cacheLessonTitle: (
    messageId: string,
    domain: string,
    focus: string,
    title: string,
  ) => {
    const normalizedTitle = title?.trim();
    if (!normalizedTitle) return;
    void domain;
    void focus;
    set((state) => {
      const cache = {
        ...state.lessonTitleCache,
        [messageId]: normalizedTitle,
      };
      try {
        localStorage.setItem("lessonTitleCache", JSON.stringify(cache));
      } catch {
        // ignore
      }
      return { lessonTitleCache: cache };
    });
  },

  updateTagStatus: async (messageId: string, tagText: string[]) => {
    try {
      await postMessageToTag(messageId, tagText);
    } catch (error) {
      console.error("Error updating favorite status:", error);
    }
  },

  updateFeedbackSentStatus: async (
    messageId: string,
    feedbackSent: boolean,
  ) => {
    try {
      await postMessageFeedbackSent(messageId, feedbackSent);
    } catch (error) {
      console.error("Error updating feedback sent status:", error);
      throw error;
    }
  },

  messageAudio: null,
  messageAudioId: null,
  setMessagesAudio: async (messageAudio, messageAudioId) => {
    set({ messageAudio, messageAudioId });
  },
  isOpenMiniPlayer: false,
  setIsOpenMiniPlayer: async (b) => set({ isOpenMiniPlayer: b }),
  clearMessageAudio: async () => set({ messageAudio: null }),
  handleToggleMiniPlayer: async () => {
    try {
      set((state) => ({ isOpenMiniPlayer: !state.isOpenMiniPlayer }));
    } catch (error) {
      console.error("Error toggling Mini Player", error);
    }
  },

  clearInfoPanelResults: () => {
    set({
      searchResults: [],
      messageByTag: [],
      favoriteMessages: [],
      lessons: [],
      reactedMessages: [],
    });
  },

  cleanMessageArray: async () => {
    await flushMessageCacheSave();
    clearInFlightPagination();
    bootstrapInFlight = null;
    set({
      messages: [],
      lastBootstrapAt: 0,
    });
  },

  persistMessagesToCacheFor: async (domain, focus, messageTheme) => {
    await flushMessageCacheSave();

    const { messages } = get();
    if (!messages.length) return;

    const cacheKey = buildMessageCacheKey({ domain, focus, messageTheme });
    if (!cacheKey) return;

    const toSave =
      messageTheme === "lessons"
        ? filterLessonFeedMessages(messages)
        : messages;

    if (!toSave.length) return;

    await saveMessagesToCache(cacheKey, toSave);
  },

  clearInfoMessages: () => set({ messageByTag: [] }),

  lessonsToDo: [],
  isLoadingLessonsToDo: false,
  errorLessonsToDo: null,

  fetchLessonsToDo: async () => {
    // Prevent duplicate calls if already loading
    if (get().isLoadingLessonsToDo) {
      return;
    }

    set({ isLoadingLessonsToDo: true, errorLessonsToDo: null });
    try {
      const data = await getLessonsToDo();
      set((state) => {
        const lessonTitleCache = mergeLessonTitleCache(
          state.lessonTitleCache,
          data,
        );
        try {
          localStorage.setItem(
            "lessonTitleCache",
            JSON.stringify(lessonTitleCache),
          );
        } catch {
          // ignore
        }
        return {
          lessonsToDo: data,
          isLoadingLessonsToDo: false,
          lessonTitleCache,
        };
      });
    } catch (error) {
      console.error("Error fetching lessons to do:", error);
      set({
        errorLessonsToDo: "Error fetching lessons to do",
        isLoadingLessonsToDo: false,
      });
    }
  },

  crashCourses: [],
  isLoadingCrashCourses: false,
  errorCrashCourses: null,

  fetchCrashCourses: async () => {
    set({ isLoadingCrashCourses: true, errorCrashCourses: null });
    try {
      const data = await getCrashCoursesList();
      // Sort crash courses by date (newest first)
      const sortedData = data.sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return dateB - dateA; // Newest first
      });
      set({ crashCourses: sortedData, isLoadingCrashCourses: false });
    } catch (error) {
      console.error("Error fetching crash courses:", error);
      set({
        errorCrashCourses: "Error fetching crash courses",
        isLoadingCrashCourses: false,
      });
    }
  },

  refreshCrashCourses: async () => {
    try {
      const data = await getCrashCoursesList();
      // Sort crash courses by date (newest first)
      const sortedData = data.sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return dateB - dateA; // Newest first
      });
      set({ crashCourses: sortedData });
    } catch (error) {
      console.error("Error refreshing crash courses:", error);
    }
  },

  clearChatData: async () => {
    try {
      localStorage.removeItem("favoriteTabLabelsCache");
      localStorage.removeItem("lessonTitleCache");
      localStorage.removeItem("reactedLessonCache");
      localStorage.removeItem(LESSONS_CACHE_KEY);
    } catch {
      // ignore
    }
    clearInFlightPagination();
    await clearMessageCache();
    get().setUseHistory(null);
    bootstrapInFlight = null;
    set({
      messages: [],
      lessons: [],
      reactedMessages: [],
      lessonsByScore: [],

      favoriteMessages: [],
      favoriteTabLabelsCache: {},
      lessonTitleCache: {},
      reactedLessonCache: {},
      tagMessages: [],

      lessonsByCompetence: [],
      searchResults: [],
      messageAudio: null,
      targetMessageId: null,
      targetMessageTabId: null,
      targetMessageRequestSeq: 0,
      isLoadingOlder: false,
      isLoadingNewer: false,
      isLoadingLessons: false,
      isLoadingReactions: false,
      isLoadingLessonsByScore: false,

      isLoadingFavoriteMessages: false,
      isLoadingTagMessages: false,

      isLoadingLessonsByCompetence: false,
      isLoadingSearch: false,
      errorLessons: null,
      errorReactions: null,
      errorLessonsByScore: null,

      errorFavoriteMessages: null,
      errorTagMessages: null,

      errorLessonsByCompetence: null,
      errorSearch: null,
      isOpenMiniPlayer: false,
      canPaginateUp: true,
      isBootstrapping: false,
      lastBootstrapAt: 0,
    });
  },

  inlineMenuTitle: "",
  inlineButtons: [],
  setInlineButtons: (buttons: InlineButton[]) =>
    set({ inlineButtons: buttons }),

  fetchInlineButtons: async () => {
    try {
      const { menuTitle, items } = await getInlineButtons();
      set({ inlineMenuTitle: menuTitle, inlineButtons: items });
    } catch (error) {
      console.error("Error fetching inline buttons:", error);
    }
  },

  clearChatInfoData: async () => {
    set({ searchResults: [] });
  },
  waitForAnimation: false,
  setWaitForAnimation: (value) => {
    set({ waitForAnimation: value });
  },

  refreshScheduled: false,
  setRefreshScheduled: (isScheduled: boolean) =>
    set({ refreshScheduled: isScheduled }),
  scheduledTargetMessageId: null,
  setScheduledTargetMessageId: (messageId: string | null) =>
    set({ scheduledTargetMessageId: messageId }),
}));

export default useChatStore;
